import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Container,
    Box,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
    useTheme,
    Fade
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { logoTransparent02 } from '../../assets/images';

const Header = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const [pastHero, setPastHero] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const heroHeight = window.innerHeight; // Assuming hero height is 100vh
            const isScrolled = window.scrollY > 100;
            const isPastHero = window.scrollY > heroHeight;

            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
            if (isPastHero !== pastHero) {
                setPastHero(isPastHero);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrolled, pastHero]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const menuItems = [
        { text: 'Home', path: '/' },
        { text: 'About', path: '/about' },
        { text: 'Services', path: '/services' },
        { text: 'Careers', path: '/careers' },
        { text: 'Contact', path: '/contact' },
    ];

    const drawer = (
        <Box
            onClick={handleDrawerToggle}
            sx={{
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                padding: '1rem 0.5rem',
            }}
        >
            <Box>
                <IconButton
                    color="inherit"
                    aria-label="close drawer"
                    edge="end"
                    onClick={handleDrawerToggle}
                    sx={{ position: 'absolute', right: 16, top: 16 }}
                >
                    <CloseIcon sx={{ fontSize: 30 }} />
                </IconButton>
                <Box component={RouterLink} to="/" onClick={handleDrawerToggle} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    mb: 4
                }}>
                    <Box
                        component="img"
                        src={logoTransparent02}
                        alt="Pivotal BS Logo"
                        className="logo-glow"
                        sx={{
                            height: 40,
                            mr: 0.5,
                        }}
                    />
                    <Box>
                        <Typography
                            variant="h4"
                            noWrap
                            sx={{
                                fontWeight: 700,
                                fontSize: '1.2rem',
                                lineHeight: 1,
                                color: '#C7AE6A',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                                letterSpacing: '1px',
                            }}
                        >
                            PIVOTAL
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.5rem',
                                color: '#C7AE6A',
                                letterSpacing: '1px',
                            }}
                        >
                            BEHAVIORAL SERVICES
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <List sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {menuItems.map((item) => (
                    <ListItem
                        key={item.text}
                        component={RouterLink}
                        to={item.path}
                        onClick={handleDrawerToggle}
                        sx={{
                            textAlign: 'center',
                            color: '#000',
                            textDecoration: 'none',
                            py: 2,
                            borderRadius: '20px',
                            transition: 'all 0.3s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(45deg, #C7AE6A, #FFC300)',
                                opacity: 0,
                                transition: 'opacity 0.3s ease-in-out',
                            },
                            '&:hover': {
                                '&::before': {
                                    opacity: 1,
                                },
                                '& .MuiTypography-root': {
                                    color: '#fff',
                                },
                            },
                            ...(location.pathname === item.path && {
                                '&::before': {
                                    opacity: 1,
                                },
                                '& .MuiTypography-root': {
                                    color: '#fff',
                                    fontWeight: 700,
                                },
                            }),
                        }}
                    >
                        <ListItemText
                            primary={item.text}
                            sx={{
                                '& .MuiTypography-root': {
                                    fontWeight: 500,
                                    fontSize: '1.2rem',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    zIndex: 1,
                                }
                            }}
                        />
                    </ListItem>
                ))}
            </List>

            <Typography variant="body2" sx={{ mt: 4, opacity: 0.7, textAlign: 'center' }}>
                © {new Date().getFullYear()} Pivotal Behavioral Services. All rights reserved.
            </Typography>
        </Box>
    );

    const headerContent = (
        <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: 80, md: 100 } }}>
                <Fade in={true} timeout={1000}>
                    <Box component={RouterLink} to="/" sx={{
                        display: 'flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                        color: 'inherit',
                    }}>
                        <Box
                            component="img"
                            src={logoTransparent02}
                            alt="Pivotal BS Logo"
                            className="logo-glow"
                            sx={{
                                height: { xs: 60, md: 80 },  // Slightly reduced logo size
                                mr: 0.5,
                            }}
                        />
                        <Box className="text-container" sx={{
                      
                        }}>
                            <Typography
                                variant="h4"
                                noWrap
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.9rem', sm: '1.8rem', md: '2.2rem' },  // Reduced font sizes
                                    lineHeight: 1,
                                    color: '#C7AE6A',
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                                    letterSpacing: '1px',
                                }}
                            >
                                PIVOTAL
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                noWrap
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '0.6rem', sm: '1.8rem', md: '0.7rem' },  // Reduced font sizes
                                    color: '#C7AE6A',
                                    letterSpacing: '1px',
                                    mt: 0.5,
                                }}
                            >
                                BEHAVIORAL SERVICES
                            </Typography>
                        </Box>
                    </Box>
                </Fade>

                {isMobile ? (
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="end"
                        onClick={handleDrawerToggle}
                        sx={{ display: { md: 'none' } }}
                    >
                        <MenuIcon sx={{ fontSize: 35 }} />
                    </IconButton>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {menuItems.map((item) => (
                            <Button
                                key={item.text}
                                component={RouterLink}
                                to={item.path}
                                sx={{
                                    mx: 1,
                                    px: 2,
                                    py: 1.5,
                                    color: 'inherit',
                                    fontWeight: 500,
                                    fontSize: '1.1rem',
                                    borderRadius: '20px',
                                    transition: 'all 0.3s ease-in-out',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease-in-out',
                                    },
                                    '&:hover': {
                                        color: '#fff',
                                        '&::before': {
                                            opacity: 1,
                                        },
                                    },
                                    '& > span': {
                                        position: 'relative',
                                        zIndex: 1,
                                    },
                                    ...(location.pathname === item.path && {
                                        fontWeight: 700,
                                        '&::before': {
                                            opacity: 1,
                                        },
                                        color: '#fff',
                                    }),
                                }}
                            >
                                <span>{item.text}</span>
                            </Button>
                        ))}
                    </Box>
                )}
            </Toolbar>
        </Container>
    );

    return (
        <>
            <AppBar position="fixed" sx={{
                bgcolor: theme.palette.header.background,
                color: theme.palette.header.foreground,
                boxShadow: 2,
                top: 0,
                transition: 'transform 0.3s ease-in-out',
                transform: scrolled ? 'translateY(-100%)' : 'translateY(0)',
            }}>
                {headerContent}
            </AppBar>
            {pastHero && (
                <AppBar position="fixed" sx={{
                    bgcolor: theme.palette.header.background,
                    color: theme.palette.header.foreground,
                    boxShadow: 2,
                    top: 0,
                    transition: 'transform 0.3s ease-in-out',
                    transform: scrolled ? 'translateY(0)' : 'translateY(-100%)',
                }}>
                    {headerContent}
                </AppBar>
            )}
            <Toolbar />
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: { xs: '50%', sm: '50%' } },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Header;